import { useElementSize } from "@mantine/hooks";
import type { ReactNode } from "react";
import { t } from "ttag";

import { FieldSet } from "metabase/common/components/FieldSet";
import { Box, Ellipsified, Group, Icon, Text, rem } from "metabase/ui";
import type { IconName } from "metabase-types/api";

import { Input } from "./Input";
import S from "./NameDescriptionInput.module.css";
import { Textarea } from "./Textarea";

interface Props {
  name: string;
  nameIcon: IconName;
  nameMaxLength?: number;
  namePlaceholder: string;
  namePrefix?: string;
  nameRightSection?: ReactNode;
  description: string;
  descriptionPlaceholder: string;
  readOnly?: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
}

export const NameDescriptionInput = ({
  name,
  nameIcon,
  nameMaxLength,
  namePlaceholder,
  namePrefix,
  nameRightSection,
  description,
  descriptionPlaceholder,
  readOnly,
  onNameChange,
  onDescriptionChange,
}: Props) => {
  const { ref, width } = useElementSize();
  const { ref: sectionRef, width: sectionWidth } = useElementSize();
  const leftSectionWidth = Math.max(sectionWidth, 40);

  const handleDescriptionChange = (value: string) => {
    const newDescription = value.trim();

    if (description !== newDescription) {
      onDescriptionChange(newDescription);
    }
  };

  const handleNameChange = (value: string) => {
    const newName = value.trim();

    if (name !== newName) {
      onNameChange(newName);
    }
  };

  return (
    <Box ref={ref}>
      {/* Name + description of the same entity -- WCAG 1.3.1, input_fields_grouped --
          in a real, visually-hidden-legend <fieldset> so sighted users see no
          layout change (the FieldSet component's `unstyled` opt-in). */}
      <FieldSet legend={t`Name, Description`} legendVisuallyHidden unstyled>
        <Input
          classNames={{
            input: S.nameInput,
            root: S.name,
            section: S.section,
          }}
          fw="bold"
          leftSection={
            <Group
              align="center"
              c="text-disabled"
              gap={10}
              flex="1"
              fs="lg"
              lh="normal"
              maw={rem(Math.floor(width / 2))}
              px={rem(10)}
              ref={sectionRef}
              wrap="nowrap"
            >
              <Icon flex="0 0 auto" name={nameIcon} size={20} c="core-brand" />

              {namePrefix && (
                <Ellipsified
                  data-testid="name-prefix"
                  lines={1}
                  tooltip={namePrefix}
                >
                  <Text c="text-disabled" component="span" size="lg">
                    {namePrefix}
                    {":"}
                  </Text>
                </Ellipsified>
              )}
            </Group>
          }
          leftSectionWidth={leftSectionWidth}
          normalize={(newValue) => {
            if (typeof newValue !== "string") {
              return name;
            }

            const isNewValueEmpty = newValue.trim().length === 0;
            return isNewValueEmpty ? name : newValue.trim();
          }}
          maxLength={nameMaxLength}
          placeholder={namePlaceholder}
          required
          rightSection={nameRightSection}
          size="lg"
          styles={{
            section: {
              // limit the flicker when element is being measured for the first time
              visibility: sectionWidth === 0 ? "hidden" : undefined,
            },
            input: {
              paddingLeft: leftSectionWidth,
            },
          }}
          value={name}
          readOnly={readOnly}
          onChange={handleNameChange}
        />

        <Textarea
          autosize
          classNames={{
            input: S.descriptionInput,
            root: S.description,
          }}
          maxRows={4}
          minRows={2}
          placeholder={descriptionPlaceholder}
          value={description}
          readOnly={readOnly}
          onChange={handleDescriptionChange}
        />
      </FieldSet>
    </Box>
  );
};
